import React, { useState, useEffect } from "react";
import { 
  Grid, 
  Paper, 
  Typography, 
  IconButton, 
  TextField, 
  MenuItem, 
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  makeStyles,
  Collapse,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  Tabs,
  Tab,
  Badge
} from '@material-ui/core';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Bookmarks as BookmarksIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as GasIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Speed as SpeedIcon,
  Build as BuildIcon,
  CloudDownload as ImportIcon,
  InfoOutlined as InfoIcon
} from "@material-ui/icons";
import axios from "axios";
import { connect } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  vehicleCard: {
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[4]
    }
  },
  vehicleList: {
    maxHeight: 400,
    overflow: 'auto',
    marginTop: theme.spacing(2)
  },
  yearRangeField: {
    marginLeft: theme.spacing(1),
    width: 80
  },
  chip: {
    margin: theme.spacing(0.5)
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  searchInput: {
    flex: 1
  },
  yearRangeContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  filterButton: {
    marginLeft: theme.spacing(1)
  },
  savedVehicleButton: {
    marginLeft: theme.spacing(1)
  },
  vehicleCount: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  noVehicles: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  loadingMessage: {
    textAlign: 'center',
    padding: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  engineBadge: {
    marginLeft: theme.spacing(1),
    fontSize: '0.75rem',
    padding: '2px 8px'
  },
  fuelTypeChip: {
    marginLeft: theme.spacing(1),
    height: 24,
    fontSize: '0.75rem'
  },
  groupHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  groupCount: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 12,
    padding: '2px 8px',
    fontSize: '0.875rem',
    marginLeft: theme.spacing(1)
  },
  vehicleDetails: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  importButton: {
    marginBottom: theme.spacing(2)
  },
  tabPanel: {
    marginTop: theme.spacing(2)
  }
}));

// Utility for creating a range of years
const createYearRange = (start, end) => {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// API request function
const apiRequest = async (query, url) => {
  try {
    const resp = await axios.post(`${url}/api/equipment/nhtsaapirequest`, query);
    return resp;
  } catch (e) {
    console.error('Error making api request:', e);
    return { data: [] };
  }
};

// Helper function to detect vehicle data format
const isOreillyFormat = (vehicle) => {
  return vehicle.hasOwnProperty('yearStart') && vehicle.hasOwnProperty('yearEnd');
};

// Helper to get fuel type icon/color
const getFuelTypeColor = (fuelType) => {
  switch(fuelType?.toUpperCase()) {
    case 'DIESEL': return '#1976d2';
    case 'GAS': return '#4caf50';
    case 'HYBRID': return '#ff9800';
    case 'ELECTRIC': return '#9c27b0';
    case 'FLEX': return '#607d8b';
    default: return '#757575';
  }
};

const EnhancedCompatibleVehicles = ({ url, notification, inventoryItem, setCompatibleVehicles }) => {
  const classes = useStyles();
  const [selectedYearRange, setSelectedYearRange] = useState("single");
  const [yearStart, setYearStart] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [engineInfo, setEngineInfo] = useState({
    engineSize: '',
    engineCode: '',
    vinCode: '',
    fuelType: 'GAS'
  });
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [tabValue, setTabValue] = useState(0);
  
  // Generate years
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1979 }, (_, i) => 1980 + i).reverse();
  
  // Filter vehicles based on search query
  const filteredVehicles = inventoryItem?.compatibleVehicles?.filter(vehicle => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Handle both formats
    if (isOreillyFormat(vehicle)) {
      return (
        vehicle.yearStart?.toString()?.includes(query) ||
        vehicle.yearEnd?.toString()?.includes(query) ||
        vehicle.make?.toLowerCase()?.includes(query) || 
        vehicle.model?.toLowerCase()?.includes(query) ||
        vehicle.engineSize?.toLowerCase()?.includes(query) ||
        vehicle.engineCode?.toLowerCase()?.includes(query) ||
        vehicle.vinCode?.toLowerCase()?.includes(query) ||
        vehicle.fuelType?.toLowerCase()?.includes(query)
      );
    } else {
      return (
        vehicle.year?.toString()?.includes(query) || 
        vehicle.make?.toLowerCase()?.includes(query) || 
        vehicle.model?.toLowerCase()?.includes(query)
      );
    }
  });

  // Fetch Makes by Year
  const fetchMakesByYear = async (year) => {
    setIsLoading(true);
    
    try {
      const resp = await apiRequest(
        { type: 'makesByYear', year: year },
        url
      );
      
      if (resp?.data?.length) {
        setMakes(resp.data);
      } else {
        setMakes([]);
      }
    } catch (error) {
      console.error('Error fetching makes:', error);
      setMakes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Models by Make and Year
  const fetchModelsByMakeAndYear = async (make, year) => {
    setIsLoading(true);
    
    try {
      const resp = await apiRequest(
        { type: 'fetchModelsByMakeAndYear', make, year },
        url
      );
      
      if (resp?.data?.models?.length) {
        let cleanModels = [];
        resp.data.models.forEach(md => {
          let index = cleanModels.findIndex(it => (it.Model_ID === md.Model_ID));
          if (index < 0) cleanModels.push(md);
        });
        setModels(cleanModels);
      } else {
        setModels([]);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Makes when Year is selected
  useEffect(() => {
    if (!yearStart) return;
    fetchMakesByYear(yearStart);
    setMake("");
    setModel("");
    setModels([]);
  }, [yearStart]);

  // Fetch Models when Make is selected
  useEffect(() => {
    if (!make || !yearStart) return;
    fetchModelsByMakeAndYear(make, yearStart);
  }, [make, yearStart]);

  // Import O'Reilly fitment data
  const handleImportFitment = async () => {
    if (!inventoryItem?.partNumber && !inventoryItem?.barcode) {
      notification({ 
        type: 'warning', 
        title: 'Warning', 
        message: 'No part number available for fitment lookup' 
      });
      return;
    }

    setImportLoading(true);
    
    try {
      const partNumber = inventoryItem.partNumber || inventoryItem.barcode;
      const response = await axios.get(`${url}/api/shop/fitment/${partNumber}`);
      
      if (response.data.success && response.data.vehicles?.length > 0) {
        // Merge with existing vehicles, avoiding duplicates
        const existingVehicles = inventoryItem?.compatibleVehicles || [];
        const newVehicles = response.data.vehicles;
        
        // Create a unique key for comparison
        const createKey = (v) => {
          if (isOreillyFormat(v)) {
            return `${v.make}-${v.model}-${v.yearStart}-${v.yearEnd}-${v.engineCode || ''}-${v.vinCode || ''}`;
          }
          return `${v.make}-${v.model}-${v.year}`;
        };
        
        const existingKeys = new Set(existingVehicles.map(createKey));
        const uniqueNewVehicles = newVehicles.filter(v => !existingKeys.has(createKey(v)));
        
        if (uniqueNewVehicles.length === 0) {
          notification({ 
            type: 'info', 
            title: 'Info', 
            message: 'All O\'Reilly vehicles are already in the list' 
          });
        } else {
          setCompatibleVehicles([...existingVehicles, ...uniqueNewVehicles]);
          notification({ 
            type: 'success', 
            title: 'Success', 
            message: `Imported ${uniqueNewVehicles.length} vehicles from O'Reilly` 
          });
        }
      } else {
        notification({ 
          type: 'warning', 
          title: 'No Data', 
          message: response.data.metadata?.hasProductButNoFitment 
            ? 'Product found but no fitment data available in O\'Reilly system'
            : 'No fitment data found for this part number' 
        });
      }
    } catch (error) {
      console.error('Error importing fitment:', error);
      notification({ 
        type: 'error', 
        title: 'Error', 
        message: 'Failed to import fitment data from O\'Reilly' 
      });
    } finally {
      setImportLoading(false);
      setImportDialogOpen(false);
    }
  };

  // Add compatible vehicle (supporting both formats)
  const addCompatibleVehicle = () => {
    if (selectedYearRange === "single") {
      if (!yearStart || !make || !model) return;
      
      const newVehicle = { 
        year: parseInt(yearStart),
        make: make.trim(), 
        model: model.trim(),
        notes: engineInfo.engineSize ? `${engineInfo.engineSize} ${engineInfo.fuelType}` : ''
      };
      
      setCompatibleVehicles([...inventoryItem?.compatibleVehicles || [], newVehicle]);
    } else {
      if (!yearStart || !yearEnd || !make || !model) return;
      
      // Create O'Reilly format vehicle
      const newVehicle = {
        yearStart: parseInt(yearStart),
        yearEnd: parseInt(yearEnd),
        make: make.trim(),
        model: model.trim(),
        engineSize: engineInfo.engineSize,
        engineCode: engineInfo.engineCode,
        vinCode: engineInfo.vinCode,
        fuelType: engineInfo.fuelType,
        description: `${make} ${model} ${engineInfo.engineSize ? engineInfo.engineSize + ' ' : ''}${engineInfo.fuelType}`
      };
      
      setCompatibleVehicles([...inventoryItem?.compatibleVehicles || [], newVehicle]);
    }
    
    // Reset form
    setYearStart("");
    setYearEnd("");
    setMake("");
    setModel("");
    setEngineInfo({
      engineSize: '',
      engineCode: '',
      vinCode: '',
      fuelType: 'GAS'
    });
    setMakes([]);
    setModels([]);
  };

  // Remove a compatible vehicle
  const removeCompatibleVehicle = (index) => {
    setCompatibleVehicles(
      (inventoryItem?.compatibleVehicles || []).filter((_, i) => i !== index)
    );
  };

  // Group vehicles by make and model
  const groupVehicles = () => {
    const groups = {};
    
    (filteredVehicles || []).forEach((vehicle, index) => {
      const makeKey = vehicle.make;
      if (!groups[makeKey]) {
        groups[makeKey] = {};
      }
      
      const modelKey = vehicle.model;
      if (!groups[makeKey][modelKey]) {
        groups[makeKey][modelKey] = [];
      }
      
      groups[makeKey][modelKey].push({ ...vehicle, originalIndex: index });
    });
    
    return groups;
  };

  // Toggle group expansion
  const toggleGroup = (makeKey, modelKey = null) => {
    const key = modelKey ? `${makeKey}-${modelKey}` : makeKey;
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const vehicleGroups = groupVehicles();
  
  // Render a single vehicle item
  const renderVehicleItem = (vehicle, index) => {
    const isOreilly = isOreillyFormat(vehicle);
    
    return (
      <Card key={index} className={classes.vehicleCard} variant="outlined">
        <CardContent>
          <Grid container alignItems="center" justify="space-between">
            <Grid item xs={11}>
              <Typography variant="body1">
                <strong>
                  {isOreilly 
                    ? `${vehicle.yearStart}-${vehicle.yearEnd}` 
                    : vehicle.year
                  } {vehicle.make} {vehicle.model}
                </strong>
                
                {isOreilly && vehicle.engineSize && (
                  <Chip 
                    label={vehicle.engineSize}
                    size="small"
                    icon={<BuildIcon />}
                    className={classes.engineBadge}
                    variant="outlined"
                  />
                )}
                
                {vehicle.fuelType && (
                  <Chip
                    label={vehicle.fuelType}
                    size="small"
                    icon={<GasIcon />}
                    className={classes.fuelTypeChip}
                    style={{ 
                      backgroundColor: getFuelTypeColor(vehicle.fuelType),
                      color: 'white'
                    }}
                  />
                )}
              </Typography>
              
              {isOreilly && (vehicle.vinCode || vehicle.engineCode) && (
                <Box className={classes.vehicleDetails}>
                  <Grid container spacing={2}>
                    {vehicle.vinCode && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          VIN Code (8th char): <strong>{vehicle.vinCode}</strong>
                        </Typography>
                      </Grid>
                    )}
                    {vehicle.engineCode && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Engine Code: <strong>{vehicle.engineCode}</strong>
                        </Typography>
                      </Grid>
                    )}
                    {vehicle.description && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                          {vehicle.description}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
              
              {vehicle.notes && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  Notes: {vehicle.notes}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={1} style={{ textAlign: 'right' }}>
              <IconButton 
                size="small" 
                onClick={() => removeCompatibleVehicle(vehicle.originalIndex)}
                aria-label="remove vehicle"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container alignItems="center" justify="space-between">
            <Grid item>
              <Typography variant="h6" gutterBottom>
                Compatible Vehicles
                {filteredVehicles?.length > 0 && (
                  <span className={classes.vehicleCount}>
                    ({filteredVehicles.length} vehicles)
                  </span>
                )}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title="Import fitment data from O'Reilly">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={importLoading ? <CircularProgress size={20} /> : <ImportIcon />}
                  onClick={() => setImportDialogOpen(true)}
                  disabled={importLoading || (!inventoryItem?.partNumber && !inventoryItem?.barcode)}
                  className={classes.importButton}
                >
                  Import from O'Reilly
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
          
          {/* Search Bar */}
          {inventoryItem?.compatibleVehicles?.length > 0 && (
            <div className={classes.searchBox}>
              <TextField
                className={classes.searchInput}
                label="Search vehicles"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" style={{ marginRight: 8 }} />
                }}
              />
            </div>
          )}
          
          <Divider className={classes.divider} />
          
          {/* Tabs for Manual Entry vs Imported */}
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Manual Entry" />
            <Tab label="Engine Details (Advanced)" />
          </Tabs>
          
          {/* Tab Panel 0: Basic Manual Entry */}
          {tabValue === 0 && (
            <Box className={classes.tabPanel}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Year Selection</InputLabel>
                    <Select
                      value={selectedYearRange}
                      onChange={(e) => setSelectedYearRange(e.target.value)}
                      label="Year Selection"
                    >
                      <MenuItem value="single">Single Year</MenuItem>
                      <MenuItem value="range">Year Range</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  {selectedYearRange === "single" ? (
                    <TextField
                      select
                      label="Year"
                      value={yearStart}
                      onChange={(e) => setYearStart(e.target.value)}
                      fullWidth
                      variant="outlined"
                    >
                      {years.map((yr) => (
                        <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <div className={classes.yearRangeContainer}>
                      <TextField
                        select
                        label="From"
                        value={yearStart}
                        onChange={(e) => setYearStart(e.target.value)}
                        fullWidth
                        variant="outlined"
                      >
                        {years.map((yr) => (
                          <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                        ))}
                      </TextField>
                      
                      <TextField
                        select
                        label="To"
                        value={yearEnd}
                        onChange={(e) => setYearEnd(e.target.value)}
                        className={classes.yearRangeField}
                        variant="outlined"
                        disabled={!yearStart}
                      >
                        {years
                          .filter(yr => yr >= parseInt(yearStart || 0))
                          .map((yr) => (
                            <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                          ))
                        }
                      </TextField>
                    </div>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    label="Make"
                    value={make}
                    onChange={(e) => setMake(e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={!yearStart || makes.length === 0}
                  >
                    <MenuItem value="">
                      <em>Select a make</em>
                    </MenuItem>
                    {makes.map((m) => (
                      <MenuItem key={m.MakeId} value={m?.MakeName}>{m?.MakeName}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    label="Model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={!make || models.length === 0}
                  >
                    <MenuItem value="">
                      <em>Select a model</em>
                    </MenuItem>
                    {models.map((m) => (
                      <MenuItem key={m.Model_ID} value={m?.Model_Name}>{m?.Model_Name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={addCompatibleVehicle}
                    disabled={
                      isLoading ||
                      (selectedYearRange === "single" 
                        ? (!yearStart || !make || !model)
                        : (!yearStart || !yearEnd || !make || !model))
                    }
                    style={{ height: 56 }}
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Tab Panel 1: Advanced Engine Details */}
          {tabValue === 1 && (
            <Box className={classes.tabPanel}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Add detailed engine information for more precise fitment (O'Reilly format)
              </Typography>
              
              <Grid container spacing={2}>
                {/* Year range selection */}
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    label="Year Start"
                    value={yearStart}
                    onChange={(e) => setYearStart(e.target.value)}
                    fullWidth
                    variant="outlined"
                  >
                    {years.map((yr) => (
                      <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    label="Year End"
                    value={yearEnd}
                    onChange={(e) => setYearEnd(e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={!yearStart}
                  >
                    {years
                      .filter(yr => yr >= parseInt(yearStart || 0))
                      .map((yr) => (
                        <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Make"
                    value={make}
                    onChange={(e) => setMake(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Engine Size"
                    value={engineInfo.engineSize}
                    onChange={(e) => setEngineInfo({...engineInfo, engineSize: e.target.value})}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., 5.3L"
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Fuel Type</InputLabel>
                    <Select
                      value={engineInfo.fuelType}
                      onChange={(e) => setEngineInfo({...engineInfo, fuelType: e.target.value})}
                      label="Fuel Type"
                    >
                      <MenuItem value="GAS">Gasoline</MenuItem>
                      <MenuItem value="DIESEL">Diesel</MenuItem>
                      <MenuItem value="HYBRID">Hybrid</MenuItem>
                      <MenuItem value="ELECTRIC">Electric</MenuItem>
                      <MenuItem value="FLEX">Flex Fuel</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="VIN Code (8th character)"
                    value={engineInfo.vinCode}
                    onChange={(e) => setEngineInfo({...engineInfo, vinCode: e.target.value.toUpperCase()})}
                    fullWidth
                    variant="outlined"
                    inputProps={{ maxLength: 1 }}
                    helperText="Enter the 8th character of the VIN"
                  />
                </Grid>
                
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Engine Code (RPO)"
                    value={engineInfo.engineCode}
                    onChange={(e) => setEngineInfo({...engineInfo, engineCode: e.target.value.toUpperCase()})}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., L5P, L83"
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={addCompatibleVehicle}
                    disabled={!yearStart || !yearEnd || !make || !model}
                    style={{ height: 56 }}
                  >
                    Add Vehicle
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {isLoading && (
            <Typography variant="body2" className={classes.loadingMessage}>
              Loading data from NHTSA database...
            </Typography>
          )}
          
          <Divider className={classes.divider} />
          
          {/* Display compatible vehicles */}
          {filteredVehicles?.length > 0 ? (
            <div className={classes.vehicleList}>
              {Object.keys(vehicleGroups).sort().map(makeKey => {
                const makeExpanded = expandedGroups[makeKey] !== false;
                const makeVehicleCount = Object.values(vehicleGroups[makeKey])
                  .reduce((sum, models) => sum + models.length, 0);
                
                return (
                  <div key={makeKey} style={{ marginBottom: 16 }}>
                    <div 
                      className={classes.groupHeader}
                      onClick={() => toggleGroup(makeKey)}
                    >
                      <Grid container alignItems="center" justify="space-between">
                        <Grid item>
                          <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                            <CarIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {makeKey}
                            <span className={classes.groupCount}>{makeVehicleCount}</span>
                          </Typography>
                        </Grid>
                        <Grid item>
                          {makeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Grid>
                      </Grid>
                    </div>
                    
                    <Collapse in={makeExpanded}>
                      {Object.keys(vehicleGroups[makeKey]).sort().map(modelKey => {
                        const vehicles = vehicleGroups[makeKey][modelKey];
                        
                        return (
                          <Box key={modelKey} style={{ marginLeft: 24, marginTop: 8 }}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              {modelKey} ({vehicles.length})
                            </Typography>
                            {vehicles.map((vehicle) => renderVehicleItem(vehicle, vehicle.originalIndex))}
                          </Box>
                        );
                      })}
                    </Collapse>
                  </div>
                );
              })}
            </div>
          ) : (
            <Paper variant="outlined" className={classes.noVehicles}>
              <Typography>No compatible vehicles added yet.</Typography>
              {inventoryItem?.partNumber && (
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  Try importing fitment data from O'Reilly using the button above.
                </Typography>
              )}
            </Paper>
          )}
        </Paper>
      </Grid>
      
      {/* Import Confirmation Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import O'Reilly Fitment Data</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will fetch vehicle compatibility data from O'Reilly for:
          </Typography>
          <Typography variant="h6" style={{ margin: '16px 0' }}>
            {inventoryItem?.partNumber || inventoryItem?.barcode}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The imported data will include detailed engine information, VIN codes, 
            and year ranges when available.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button 
            onClick={handleImportFitment} 
            color="primary" 
            variant="contained"
            disabled={importLoading}
          >
            {importLoading ? 'Importing...' : 'Import Fitment Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    url: state.url,
    user: state.user,
    notification: state.notification,
    perms: state.perms
  };
};

export default connect(mapStateToProps)(EnhancedCompatibleVehicles);
